import * as THREE from 'three';

export interface IThreeContextData {
    width: number;
    height: number;
    scene: THREE.Scene;
    renderer: THREE.WebGLRenderer;
    camera: THREE.PerspectiveCamera;
    canvas: HTMLCanvasElement;
    container: HTMLDivElement;
    time: number;
    deltaTime: number;
}

export interface IThreeContext {
    isDisposed(): boolean;
    onWindowResize(width: number, height: number): void;
    dispose(): void;
}

export class ThreeContext<TState> implements IThreeContext {

    private width: number;
    private height: number;
    private scene: THREE.Scene;
    private renderer: THREE.WebGLRenderer;
    private camera: THREE.PerspectiveCamera;
    private canvas: HTMLCanvasElement;
    private container: HTMLDivElement;
    private state: TState;

    private onStart: (context: IThreeContextData) => TState;
    private onUpdate: (context: IThreeContextData, state: TState) => void;
    private timeLastFrame: number;
    private isContextDisposed: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        container: HTMLDivElement,
        width: number,
        height: number,
        onStart: (context: IThreeContextData) => TState,
        onUpdate: (context: IThreeContextData, state: TState) => void
    ) {
        this.canvas = canvas;
        this.container = container;

        this.width = width;
        this.height = height;

        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({
            canvas: canvas,
            antialias: true,
        });

        this.camera = new THREE.PerspectiveCamera(45, this.width / this.height, 0.1, 10000);

        this.onStart = onStart;
        this.onUpdate = onUpdate;
        this.timeLastFrame = 0;

        // RUN
        this.onWindowResize(width, height);
        this.state = this.onStart(this.getContextData(0, 0));
        this.update(0);
    }

    public isDisposed() {
        return this.isContextDisposed;
    }

    public dispose() {
        this.renderer.dispose();
        this.isContextDisposed = true;
    }

    public onWindowResize(width: number, height: number) {
        this.width = width;
        this.height = height;
        this.camera.aspect = this.width / this.height;
        this.camera.updateProjectionMatrix();
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(width, height);

        this.renderer.setViewport(0, 0, width, height);
        this.renderer.setScissor(0, 0, width, height);
        this.renderer.setScissorTest(true);
    }

    private update(time: number) {
        const deltaTime = Math.min(100, time - this.timeLastFrame);
        this.timeLastFrame = time;

        this.onUpdate(this.getContextData(time, deltaTime), this.state);
        this.renderer.render(this.scene, this.camera);

        if (!this.isContextDisposed) {
            requestAnimationFrame(this.update.bind(this));
        }
    }

    private getContextData(time: number, deltaTime: number): IThreeContextData {
        return {
            width: this.width,
            height: this.height,
            scene: this.scene,
            renderer: this.renderer,
            camera: this.camera,
            canvas: this.canvas,
            container: this.container,
            time: time,
            deltaTime: deltaTime,
        };
    }
}